/**
 * MyDaemonKeySection - "My daemon key": every confirmed workspace member
 * creates or regenerates their own personal MCP key from here.
 *
 * track:workspace-key-onboarding (decision #48248) — supersedes #48235's
 * shared-workspace-key design. POSTs /api/workspace/member-key (any
 * confirmed member, Firebase-authenticated human only — see server.ts's
 * requireHumanCaller). The server always revokes any existing key for the
 * caller first, then mints a fresh one (create and regenerate are the same
 * operation; `status` in the response only changes the wording here). Shown
 * to every member, not just the Lead — unlike the shared workspace connection
 * in ConnectYourAiSection, which stays Lead-only. The new key and URL are
 * handed to `onCreated`; ConnectYourAiSection shows them (revealed once).
 */
import { useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import type { CreatedKey } from "./ConnectYourAiSection";

interface MemberKeyResponse {
  mcp_url: string;
  status: "created" | "regenerated";
  error?: string;
}

export function MyDaemonKeySection({ workspaceId = "default", onCreated }: { workspaceId?: string; onCreated?: (created: CreatedKey) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreateOrRegenerate() {
    if (!confirm(
      "If you already have a daemon key, creating a new one stops the old one everywhere, immediately. Paste the new key into Relay Helper (Settings → Daemon key) and update any AI advisor connection. Continue?"
    )) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/member-key", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
      });
      const data: MemberKeyResponse = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        return;
      }
      onCreated?.({ mcpUrl: data.mcp_url, status: data.status });
    } catch (err) {
      console.error("[MyDaemonKeySection] Failed to create/regenerate key:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="team-section-my-key" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">My Daemon Key</div>
      <div style={{ fontSize: 13, color: "var(--muted)", fontFamily: "var(--font-ui)", marginBottom: 12 }}>
        Your personal key. Relay Helper and any AI advisor you connect use it to act as you. It is shown only once, in Connect your AI above, when you create it. Creating a new one stops the old one everywhere: paste the new key into Relay Helper (Settings → Daemon key) and update any AI advisor connection.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn sm" onClick={handleCreateOrRegenerate} disabled={loading}>
          {loading ? "Working..." : "Create or regenerate my daemon key"}
        </button>
      </div>

      {error && <div className="sc-callout error" style={{ marginTop: 12 }}>{error}</div>}
    </section>
  );
}
